import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, PackageCheck, Images } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatErr, rupiah } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Common";

const PackageEditor = ({ event, onSaved }) => {
  const [bundle, setBundle] = useState(event.bundle_price ?? "");
  const [rows, setRows] = useState((event.packages || []).map((p) => ({ count: p.count, price: p.price })));

  useEffect(() => {
    setBundle(event.bundle_price ?? "");
    setRows((event.packages || []).map((p) => ({ count: p.count, price: p.price })));
  }, [event]);

  const setRow = (i, k, v) => setRows((prev) => prev.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const save = async () => {
    try {
      const { data } = await api.put(`/events/${event.id}/packages`, {
        bundle_price: bundle ? Number(bundle) : null,
        packages: rows.filter((r) => r.count && r.price).map((r) => ({ count: Number(r.count), price: Number(r.price) })),
      });
      onSaved(data);
      toast.success("Paket disimpan");
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="package-editor">
      <section className="rounded-2xl bg-white border border-zinc-200 p-6">
        <p className="text-xs uppercase tracking-widest text-zinc-400 font-bold mb-1">Paket isi tetap</p>
        <p className="text-sm text-zinc-500 mb-5">Mis. 3 foto Rp 60.000. Diterapkan otomatis di keranjang member dengan kombinasi termurah. Harga satuan event: <span className="font-mono-x">{rupiah(event.price)}</span>.</p>
        <div className="space-y-3">
          {rows.map((r, i) => {
            const normal = Number(r.count || 0) * event.price;
            const save = normal - Number(r.price || 0);
            return (
              <div key={i} className="flex flex-wrap items-end gap-3" data-testid={`package-row-${i}`}>
                <div className="space-y-1"><Label className="text-xs">Jumlah foto</Label><Input type="number" min={2} value={r.count} onChange={(e) => setRow(i, "count", e.target.value)} className="w-28 h-10" data-testid={`package-count-input-${i}`} /></div>
                <div className="space-y-1"><Label className="text-xs">Harga paket (Rp)</Label><Input type="number" min={1} value={r.price} onChange={(e) => setRow(i, "price", e.target.value)} className="w-40 h-10" data-testid={`package-price-input-${i}`} /></div>
                <p className={`text-xs pb-3 ${save > 0 ? "text-emerald-700" : "text-rose-600"}`}>{r.count && r.price ? (save > 0 ? `Hemat ${rupiah(save)} dari ${rupiah(normal)}` : "Harus lebih murah dari harga satuan") : ""}</p>
                <Button variant="ghost" size="icon" onClick={() => setRows((prev) => prev.filter((_, j) => j !== i))} className="ml-auto" data-testid={`package-remove-${i}`}><Trash2 className="h-4 w-4 text-zinc-500" /></Button>
              </div>
            );
          })}
        </div>
        <Button variant="outline" onClick={() => setRows((prev) => [...prev, { count: (prev.at(-1)?.count || 1) * 1 + 2, price: "" }])} className="mt-4 rounded-xl" data-testid="package-add-button"><Plus className="mr-1.5 h-4 w-4" />Tambah paket</Button>
      </section>
      <section className="rounded-2xl bg-white border border-zinc-200 p-6">
        <p className="text-xs uppercase tracking-widest text-zinc-400 font-bold mb-1">Paket semua foto wajahmu</p>
        <p className="text-sm text-zinc-500 mb-4">Satu harga untuk semua foto yang cocok dengan wajah member di event ini, termasuk foto baru yang diunggah setelahnya. Kosongkan untuk menonaktifkan.</p>
        <Input type="number" min={0} placeholder="Mis. 100000" value={bundle} onChange={(e) => setBundle(e.target.value)} className="w-48 h-10" data-testid="package-bundle-input" />
      </section>
      <Button onClick={save} className="rounded-xl h-11 bg-blue-600 hover:bg-blue-700 px-8" data-testid="package-save-button">Simpan paket</Button>
    </div>
  );
};

export default function PhotographerPackages() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [events, setEvents] = useState(null);
  const selectedId = params.get("event");

  useEffect(() => {
    api.get("/events", { params: { photographer_id: user.id } }).then((r) => setEvents(r.data));
  }, [user.id]);

  const selected = events?.find((e) => e.id === selectedId) || events?.[0];
  const update = (ev) => setEvents((prev) => prev.map((e) => (e.id === ev.id ? ev : e)));

  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <PageHeader label="Harga bundling" title="Paket Foto" />
      {events === null ? <p className="text-zinc-500">Memuat...</p> : events.length === 0 ? (
        <p className="text-zinc-500" data-testid="packages-no-events">Buat event terlebih dahulu.</p>
      ) : (
        <div className="grid lg:grid-cols-12 gap-8">
          <aside className="lg:col-span-4 space-y-2" data-testid="packages-event-list">
            {events.map((e) => {
              const n = (e.packages || []).length + (e.bundle_price ? 1 : 0);
              return (
                <button key={e.id} onClick={() => setParams({ event: e.id })} data-testid={`packages-event-${e.id}`}
                  className={`w-full text-left rounded-xl border p-4 transition-colors ${selected?.id === e.id ? "border-blue-500 bg-blue-50/50" : "border-zinc-200 bg-white hover:border-zinc-300"}`}>
                  <p className="font-semibold truncate">{e.title}</p>
                  <p className="text-xs text-zinc-500 mt-1 flex items-center gap-3">
                    <span className="inline-flex items-center gap-1"><Images className="h-3 w-3" />{e.photo_count}</span>
                    <span className="inline-flex items-center gap-1"><PackageCheck className="h-3 w-3" />{n} paket</span>
                  </p>
                </button>
              );
            })}
          </aside>
          <div className="lg:col-span-8">{selected && <><h2 className="text-xl font-bold mb-4" data-testid="packages-selected-title">{selected.title}</h2><PackageEditor event={selected} onSaved={update} /></>}</div>
        </div>
      )}
    </main>
  );
}
